import { computed, ComputedRef, reactive } from "vue"
import axios from "axios"
import { AvailablePaymentMethod } from "@paddle/paddle-js"

import BILLING_TYPE from "@Constants/billing-type"
import { logError } from "@Helpers/logger.js"
import { ParseServerError } from "@Modules/shared/Api/ServerErrors"
import OrderModel, { Order, OrderData } from "../Data/Models/OrderModel"

/**
 * Customer information returned from Paddle checkout initialization.
 */
export interface CheckoutCustomer {
  id: string
  auth_token: string
}

/**
 * Full checkout response from backend checkout API.
 * Combined from BillingService::checkoutInit() + Paddle/Checkout::initData()
 */
export interface CheckoutResponse {
  // From BillingService::checkoutInit()
  billing_type: string
  locale: string
  order_id: number

  // From Paddle/Checkout::initData()
  transaction_id: string
  customer: CheckoutCustomer
  success_url: string
  cancel_url: string
  allow_discounts: boolean
  payment_methods: AvailablePaymentMethod[] | null
  payment_method: string | null

  // Optional
  discount_code?: string
  session_id?: string
}

export type OrdersErrors = Record<string, string>

export type OrdersApi = {
  orders: ComputedRef<Order[]>
  loading: ComputedRef<boolean>
  errors: ComputedRef<OrdersErrors>
  getOrderById: ComputedRef<(id: number) => Order | null>
  fetchOrders(): Promise<void>
  fetchOrdersForOrganization(orgSlug: string): Promise<void>
  fetchOrder(orderId: number, options?: { disableErrorInterceptors?: boolean }): Promise<Order | null>
  fetchOrderForOrganization(orgSlug: string, orderId: number, options?: { disableErrorInterceptors?: boolean }): Promise<Order | null>
  fetchOrderBySession(session: string, options?: { disableErrorInterceptors?: boolean }): Promise<Order | null>
  fetchOrderBySessionForOrganization(orgSlug: string, session: string, options?: { disableErrorInterceptors?: boolean }): Promise<Order | null>
  checkout(instance: string): Promise<CheckoutResponse | null>
  checkoutForOrganization(orgSlug: string, instance: string): Promise<CheckoutResponse | null>
  retryCheckout(orderId: number): Promise<CheckoutResponse | null>
  getOrderPdfUrl(orderId: number): string
  getOrderPdfUrlForOrganization(orgSlug: string, orderId: number): string
  clearOrders(): void
  clearErrors(): void
  setRouter(router: Pick<HttpInterface, "get" | "post">): OrdersApi
}

interface OrdersState {
  orders: Order[]
  loading: boolean
  errors: OrdersErrors
}

export default function OrdersApi(): OrdersApi {
  const state = reactive<OrdersState>({
    orders: [],
    loading: false,
    errors: {},
  })

  let router: Pick<HttpInterface, "get" | "post"> = axios

  function clearErrors(): void {
    state.errors = {}
  }

  function setErrors(url: string, e: unknown): void {
    const { status, fieldErrors, serverMessage, exceptionMessage } = ParseServerError(e)
    logError(`server status:${status} url: ${url}`)
    const errors: OrdersErrors = { ...state.errors }

    if (fieldErrors) {
      Object.entries(fieldErrors).forEach(([key, val]) => {
        errors[key] = val[0] ?? ""
      })
    } else if (serverMessage) {
      errors.message = serverMessage
    } else if (exceptionMessage) {
      errors.message = exceptionMessage
    }

    state.errors = errors
  }

  function upsertOrder(order: Order): void {
    const index = state.orders.findIndex((o) => o.id === order.id)
    if (index === -1) {
      state.orders = [...state.orders, order]
    } else {
      state.orders = [...state.orders.slice(0, index), order, ...state.orders.slice(index + 1)]
    }
  }

  return {
    orders: computed(() => state.orders),
    loading: computed(() => state.loading),
    errors: computed(() => state.errors),

    getOrderById: computed<(id: number) => Order | null>(
      () => (id: number) => state.orders.find((o) => o.id === id) ?? null,
    ),

    async fetchOrders(): Promise<void> {
      state.loading = true
      clearErrors()

      const url = "/app/api/shopping-cart/orders"

      try {
        const response = await router.get<{ orders: OrderData[] }>(url)
        state.orders = response.data.orders.map((order) => OrderModel(order))
      } catch (e) {
        setErrors(url, e)
      } finally {
        state.loading = false
      }
    },

    async fetchOrdersForOrganization(orgSlug: string): Promise<void> {
      state.loading = true
      clearErrors()

      const url = `/app/api/organization/${orgSlug}/shopping-cart/orders`

      try {
        const response = await router.get<{ orders: OrderData[] }>(url)
        state.orders = response.data.orders.map((order) => OrderModel(order))
      } catch (e) {
        setErrors(url, e)
      } finally {
        state.loading = false
      }
    },

    async fetchOrder(
      orderId: number,
      options?: { disableErrorInterceptors?: boolean },
    ): Promise<Order | null> {
      state.loading = true
      clearErrors()

      const url = `/app/api/shopping-cart/order/${orderId}`

      try {
        const config = options?.disableErrorInterceptors
          ? { DISABLE_ERROR_INTERCEPTORS: true }
          : {}
        const response = await router.get<{ data: OrderData }>(url, config)
        const order = OrderModel(response.data.data)
        upsertOrder(order)
        return order
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    async fetchOrderForOrganization(
      orgSlug: string,
      orderId: number,
      options?: { disableErrorInterceptors?: boolean },
    ): Promise<Order | null> {
      state.loading = true
      clearErrors()

      const url = `/app/api/organization/${orgSlug}/shopping-cart/order/${orderId}`

      try {
        const config = options?.disableErrorInterceptors
          ? { DISABLE_ERROR_INTERCEPTORS: true }
          : {}
        const response = await router.get<{ data: OrderData } | OrderData>(url, config)
        const orderData = "data" in response.data ? response.data.data : response.data
        const order = OrderModel(orderData)
        upsertOrder(order)
        return order
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    async fetchOrderBySession(
      session: string,
      options?: { disableErrorInterceptors?: boolean },
    ): Promise<Order | null> {
      state.loading = true
      clearErrors()

      const url = `/app/api/shopping-cart/status/${session}`

      try {
        const config = options?.disableErrorInterceptors
          ? { DISABLE_ERROR_INTERCEPTORS: true }
          : {}
        const response = await router.get<{ data: OrderData }>(url, config)
        const order = OrderModel(response.data.data)
        upsertOrder(order)
        return order
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    async fetchOrderBySessionForOrganization(
      orgSlug: string,
      session: string,
      options?: { disableErrorInterceptors?: boolean },
    ): Promise<Order | null> {
      state.loading = true
      clearErrors()

      const url = `/app/api/organization/${orgSlug}/shopping-cart/status/${session}`

      try {
        const config = options?.disableErrorInterceptors
          ? { DISABLE_ERROR_INTERCEPTORS: true }
          : {}
        const response = await router.get<{ data: OrderData } | OrderData>(url, config)
        const orderData = "data" in response.data ? response.data.data : response.data
        const order = OrderModel(orderData)
        upsertOrder(order)
        return order
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    async checkout(instance: string): Promise<CheckoutResponse | null> {
      state.loading = true
      clearErrors()

      const url = "/app/api/shopping-cart/checkout"

      try {
        const response = await router.post<CheckoutResponse>(url, { instance })
        return response.data
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    async checkoutForOrganization(orgSlug: string, instance: string): Promise<CheckoutResponse | null> { // FIXME: Org Model passed so permission are accurate. 
      state.loading = true
      clearErrors()

      const url = `/app/api/organization/${orgSlug}/shopping-cart/checkout`

      try {
        const response = await router.post<CheckoutResponse>(url, {
          instance,
          billing_type: BILLING_TYPE.PADDLE,
        })
        return response.data
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    async retryCheckout(orderId: number): Promise<CheckoutResponse | null> {
      state.loading = true
      clearErrors()

      const url = `/app/api/shopping-cart/order/${orderId}/checkout`

      try {
        const response = await router.post<CheckoutResponse>(url, {})
        return response.data
      } catch (e) {
        setErrors(url, e)
        return null
      } finally {
        state.loading = false
      }
    },

    getOrderPdfUrl(orderId: number): string {
      return `/app/api/shopping-cart/order/${orderId}/pdf`
    },

    getOrderPdfUrlForOrganization(orgSlug: string, orderId: number): string {
      return `/app/organization/${orgSlug}/shopping-cart/order/${orderId}/pdf`
    },

    clearOrders(): void {
      state.orders = []
    },

    clearErrors,

    setRouter(newRouter) {
      router = newRouter
      return this
    },
  }
}
